import axios from 'axios';
import { getItem, KEYS, setItem } from 'storage';
import { getStore } from 'store';
import { setGlobal, setGlobalLoading } from 'store/global/reducer';
import { LOGOUT_USER } from 'store/key';
import { ABNORMAL_ACCOUNT_STATUS, MULTIPLE_LOGIN_LOGOUT } from 'utility/frontendMessage';
import { handleRefreshTokenException, updateIsRefreshToken } from 'utility/general';

const errorEndpointWhitelist = ['/user/account-activation', '/file/upload-document', '/auth/refresh'];

class BaseAPI {
    constructor() {
        this.refreshingToken = null;
        this.defaultRequestConfig = {
            timeout: 90000,
            withCredentials: false,
            headers: {
                'Content-Type': 'application/json',
            },
        };

        if (process.env.NODE_ENV === 'development') {
            //this.defaultRequestConfig.baseURL =  'http://localhost:1914/api';
            this.defaultRequestConfig.baseURL = 'http://localhost:3000/';
        } else if (process.env.NODE_ENV === 'production') {
            this.defaultRequestConfig.baseURL = process.env.REACT_APP_API_DOMAIN;
        }
    }

    handleTokenRefresh = () => {
        const refreshToken = getItem(KEYS.REFRESH_TOKEN, 'refresh token is not exist in frontend');
        const user = getStore().getState().user;

        updateIsRefreshToken(true);

        return axios
            .create(this.defaultRequestConfig)({
                method: 'POST',
                url: '/auth/refresh',
                data: {
                    refreshToken,
                    role: user.role,
                },
            })
            .then((res) => {
                // catch error when email is not exist
                handleRefreshTokenException(res);
                updateIsRefreshToken(false);

                return res;
            })
            .catch((err) => {
                updateIsRefreshToken(false);
                throw err;
            });
    };

    handleAPIError = async (error) => {
        const { response } = error;
        const store = getStore();

        if (response?.status === 500) {
            store.dispatch(
                setGlobal({
                    isErrorShow: true,
                    errorMessage: 'Internal Server Error',
                    isGlobalLoadingShow: false,
                    errorTitle: 'Error',
                }),
            );
        }

        // handle token / secret error
        if (response?.status === 409) {
            store.dispatch(
                setGlobal({
                    isErrorShow: true,
                    errorMessage: 'Please login again',
                    isGlobalLoadingShow: false,
                    errorTitle: 'Reminder',
                }),
            );

            store.dispatch({
                type: LOGOUT_USER,
                payload: {},
            });
        }

        if (response?.status === 403) {
            const errorCode = response?.data?.errorCode;

            // if no refresh token is found in the user db
            if (errorCode === 'msg.login004') {
                store.dispatch(
                    setGlobal({
                        isErrorShow: true,
                        errorMessage: MULTIPLE_LOGIN_LOGOUT,
                        isGlobalLoadingShow: false,
                        errorTitle: 'Session expired',
                    }),
                );

                store.dispatch({
                    type: LOGOUT_USER,
                    payload: {},
                });
            }
        }

        if (response?.status === 400) {
            const errorMessage = response?.data?.errorMessage;
            if (response?.data instanceof Blob) {
                const responseObj = JSON.parse(await response.data.text());
                store.dispatch(
                    setGlobal({
                        isErrorShow: true,
                        errorMessage: responseObj?.errorMessage,
                        isGlobalLoadingShow: false,
                        errorTitle: '',
                        errorButton: 'Close',
                    }),
                );
                return;
            }

            const isWhiteListEndpoint = errorEndpointWhitelist.includes(response.config.url);

            if (isWhiteListEndpoint) {
                throw error;
            }

            if (errorMessage?.includes('Full authentication is required to access this resource')) {
                window.confirm('Please login to access the resource');

                store.dispatch({
                    type: LOGOUT_USER,
                });
            } else if (errorMessage?.includes('Account is locked / disabled. Please contact HO Admin.')) {
                store.dispatch(
                    setGlobal({
                        isErrorShow: true,
                        errorMessage:
                            'Your account has reached the maximum number of failed login attempts and has been temporarily locked. Please <a href="/?passwordDisabled=true">reset your password</a> or contact HO Admin for assistance.',
                        isGlobalLoadingShow: false,
                        errorTitle: '',
                        errorButton: 'Close',
                        useDangerouslySetInnerHTML: true,
                    }),
                );
            } else {
                store.dispatch(
                    setGlobal({
                        isErrorShow: true,
                        errorMessage: errorMessage,
                        isGlobalLoadingShow: false,
                        errorTitle: 'Error',
                        errorButton: 'Close',
                        useDangerouslySetInnerHTML: true,
                    }),
                );
            }
        } else {
            store.dispatch(
                setGlobal({
                    errorTitle: 'Error from server',
                    isErrorShow: true,
                    errorMessage,
                    isGlobalLoadingShow: false,
                }),
            );
        }
    };

    sendRefreshRequest = async (requestConfig) => {
        try {
            updateIsRefreshToken(true);
            this.refreshingToken = this.refreshingToken || this.handleTokenRefresh();
            let refreshTokenResult;

            try {
                refreshTokenResult = await this.refreshingToken;
            } catch (refreshTokenError) {
                if (refreshTokenError?.response?.status === 400 || refreshTokenError?.response?.status === 500) {
                    const store = getStore();

                    store.dispatch({
                        type: LOGOUT_USER,
                    });

                    const tempMessage = refreshTokenError?.response?.data?.errorMessage;

                    if (tempMessage?.toLowerCase?.()?.includes('no multiple login')) {
                        // window.location.replace('/?post-dialog=multiple-login');
                        store.dispatch(
                            setGlobal({
                                isErrorShow: true,
                                errorTitle: 'Session expired',
                                errorMessage: MULTIPLE_LOGIN_LOGOUT,
                                isGlobalLoadingShow: false,
                            }),
                        );
                    } else {
                        updateIsRefreshToken(false);
                        setGlobalLoading(true);
                        window.location.replace('/?post-dialog=session-expired');
                    }
                }
                throw refreshTokenError;
            }

            this.refreshingToken = null; //makes refreshAccessToken can run again

            const status = refreshTokenResult.data.status;

            // refresh with status code 200 but error status
            if (status === 'ERROR') {
                const store = getStore();
                store.dispatch({
                    type: LOGOUT_USER,
                });

                store.dispatch(
                    setGlobal({
                        isErrorShow: true,
                        errorTitle: 'Session expired',
                        errorMessage: ABNORMAL_ACCOUNT_STATUS,
                        isGlobalLoadingShow: false,
                    }),
                );

                throw new Error(refreshTokenResult.data.errorMessage);
            }

            const { token: newAccessToken, refreshToken: newRefreshToken } = refreshTokenResult.data.result;

            setItem(KEYS.ACCESS_TOKEN, newAccessToken);
            setItem(KEYS.REFRESH_TOKEN, newRefreshToken);
            updateIsRefreshToken(false);
            const result = await this.authAPI(requestConfig, newAccessToken);
            return result;
        } catch (outerError) {
            console.log('105448 index.js error', outerError);
            this.refreshingToken = null; //makes refreshAccessToken can run again
            updateIsRefreshToken(false);
            throw outerError;
        }
    };

    handleTokenExpired = async (error) => {
        const { response } = error;

        if (response?.status === 409) {
            const store = getStore();
            const errorMessage = 'Please login again';

            store.dispatch(
                setGlobal({
                    isErrorShow: true,
                    errorTitle: 'Notification',
                    isGlobalLoadingShow: false,
                    errorMessage,
                }),
            );

            throw error;
        }

        if (response?.status === 401) {
            return this.sendRefreshRequest(error.config);
        }

        throw error;
    };

    basicAPI = (requestConfig) => {
        return axios.create(this.defaultRequestConfig)(requestConfig).catch(this.handleAPIError);
    };

    // default getting access token from store
    authAPI = async (requestConfig, accessToken = getItem(KEYS.ACCESS_TOKEN)) => {
        const responseType = requestConfig?.responseType;
        // if type of api is download api, refresh the token before file download
        if (responseType === 'blob') {
            const refreshTokenResult = await this.handleTokenRefresh();
            const { token: newAccessToken, refreshToken: newRefreshToken } = refreshTokenResult.data.result;
            setItem(KEYS.ACCESS_TOKEN, newAccessToken);
            setItem(KEYS.REFRESH_TOKEN, newRefreshToken);
            accessToken = newAccessToken;
        }

        if (!requestConfig.headers) {
            requestConfig.headers = { Authorization: `Bearer ${accessToken}` };
        } else {
            requestConfig.headers['Authorization'] = `Bearer ${accessToken}`;
        }

        return axios
            .create({ ...this.defaultRequestConfig })(requestConfig)
            .then((res) => {
                if (res.data?.status === 'TOKEN_REFRESH') {
                    return this.sendRefreshRequest(res.config);
                    // throw new Error('Token Expired');
                }
                return res;
            })
            .catch(this.handleTokenExpired)
            .catch(this.handleAPIError);
    };
}

export default new BaseAPI();
